import React, { useState, useEffect } from "react";
import { Switch, useHistory } from "react-router";
import { useAuth } from "../../hooks/use-auth";
import PrivateRoute from "../../widgets/PrivateRoute.jsx";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { formatFirebaseError } from "../../data/error-formatter.js";
import { useCollection } from "react-firebase-hooks/firestore";
import { makeStyles } from "@material-ui/core/styles";
import { OrganizationSelector } from "../../widgets";
import InspectionListCollapsed from "./InspectionListCollapsed";
import InspectionListExpanded from "./InspectionListExpanded";
import { NavBar } from "../../widgets";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import ControlledSnackbar from "../../widgets/ControlledSnackbar";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Button from "@material-ui/core/Button";
import MasterReportBar from "./MasterReportBar";
import MasterReportDialog from "./MasterReportDialog";
import { useDocumentDataOnce } from "react-firebase-hooks/firestore";

// Mirrors MAX_INSPECTIONS in functions/combined-report-gen.js, which enforces
// it. Kept here so an over-sized selection is refused before the round trip.
const MAX_COMBINED_INSPECTIONS = 15;

const DEFAULT_REPORT_FORMATS = ["pdf", "xlsx"];

const useStyles = makeStyles((theme) => ({
  container: {
    flex: "1 1 auto",
    display: "flex",
    flexDirection: "column",
  },
  buttonRow: {
    flex: "0 1 auto",
    display: "flex",
    flexDirection: "row",
  },
  grid: {
    flex: "1 1 auto",
  },
  create: {
    flex: "0 1 auto",
    marginBottom: theme.spacing(1),
  },
  spacer: {
    flex: "1 1 auto",
  },
  orgSelect: {
    margin: theme.spacing(2),
    width: 200,
  },
  orgSelectCollapsed: {
    marginBottom: theme.spacing(2),
    marginLeft: theme.spacing(2),
    marginTop: theme.spacing(2),
    width: 200,
  },
  title: {
    flexGrow: 1,
  },
}));

function createQuery(organization, auth) {
  let query = firebase
    .firestore()
    .collection("inspections")
    .where("organization", "==", organization.id);

  if (auth.user.claims.isAdmin || auth.user.claims.isSuperAdmin) {
    return query.orderBy("createdAt", "desc");
  } else {
    console.log(`User ${auth.authUser.uid} is not admin, adding author param`);
    return query
      .where("author", "==", auth.authUser.uid)
      .orderBy("createdAt", "desc");
  }
}

function InspectionList() {
  const classes = useStyles();
  const [auth] = useAuth();
  const history = useHistory();

  const [snackbarState, setSnackbarState] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [inspectionToDelete, setInspectionToDelete] = useState(null);
  const [organization, setOrganization] = useState({
    id: auth.user.claims.organization,
    name: auth.user.claims.organization,
  });
  const [snapshot, loading, error] = useCollection(
    createQuery(organization, auth)
  );

  const [combineMode, setCombineMode] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  const [reportType, setReportType] = useState("pdf");
  const [masterReportOpen, setMasterReportOpen] = useState(false);

  const [organizationData] = useDocumentDataOnce(
    firebase.firestore().doc(`organizations/${organization.id}`)
  );

  const isAdmin = auth.user.claims.isAdmin || auth.user.claims.isSuperAdmin;
  const reportTypes =
    organizationData?.reportSettings?.supportedFormats ??
    DEFAULT_REPORT_FORMATS;

  // A selection is a list of inspection ids belonging to one organization, so
  // switching organizations has to discard it rather than submit ids the new
  // organization does not own.
  useEffect(() => {
    setSelectionModel([]);
  }, [organization.id]);

  const handleCombineModeToggle = () => {
    setSelectionModel([]);
    setCombineMode((current) => !current);
  };

  const handleGenerateMasterReport = () => {
    setMasterReportOpen(true);
  };

  const handleMasterReportClose = () => {
    setMasterReportOpen(false);
  };

  const getError = () => {
    if (error === undefined) return undefined;

    console.log("Formatting error");
    return { message: formatFirebaseError(error) };
  };

  const handleOrganizationChange = (organization) => {
    setOrganization(organization);
  };

  const handleShowSnackbar = (snackbarState) => {
    setSnackbarState(snackbarState);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbarState({
      ...snackbarState,
      ["open"]: false,
    });
  };

  const handleItemSelected = (itemId) => {
    if (!itemId) {
      history.push("/inspections");
    } else {
      history.push(`/inspections/${itemId}`);
    }
  };

  const handleDeleteClick = (inspectionId) => {
    setInspectionToDelete(inspectionId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!inspectionToDelete) return;

    try {
      await firebase
        .firestore()
        .collection("inspections")
        .doc(inspectionToDelete)
        .update({
          isHidden: true,
          hiddenAt: firebase.firestore.FieldValue.serverTimestamp(),
          hiddenBy: auth.authUser.uid,
        });

      setSnackbarState({
        open: true,
        message: "Inspection deleted successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error deleting inspection:", error);
      setSnackbarState({
        open: true,
        message: "Failed to delete inspection",
        severity: "error",
      });
    } finally {
      setDeleteDialogOpen(false);
      setInspectionToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setInspectionToDelete(null);
  };

  return (
    <React.Fragment>
      <NavBar>
        <Toolbar>
          <Typography variant="h6" className={classes.title}>
            Inspections
          </Typography>
          {isAdmin && (
            <Button color="inherit" onClick={handleCombineModeToggle}>
              {combineMode ? "Cancel" : "Combine Reports"}
            </Button>
          )}
        </Toolbar>
      </NavBar>
      {auth.user.claims.isSuperAdmin && (
        <OrganizationSelector
          className={classes.orgSelect}
          organization={organization}
          onChange={handleOrganizationChange}
          size="small"
          variant="outlined"
        />
      )}
      <Switch>
        <PrivateRoute path="/inspections/:selectedId">
          <InspectionListCollapsed
            snapshot={snapshot}
            onItemSelected={handleItemSelected}
          />
        </PrivateRoute>
        <PrivateRoute path="/inspections">
          <InspectionListExpanded
            organizationId={organization.id}
            loading={loading}
            snapshot={snapshot}
            error={getError()}
            onItemSelected={handleItemSelected}
            onShowSnackbar={handleShowSnackbar}
            onDeleteClick={handleDeleteClick}
            isSuperAdmin={auth.user.claims.isSuperAdmin}
            combineMode={combineMode}
            selectionModel={selectionModel}
            onSelectionChange={setSelectionModel}
          />
          {combineMode && (
            <MasterReportBar
              selectedCount={selectionModel.length}
              maximum={MAX_COMBINED_INSPECTIONS}
              reportType={reportType}
              reportTypes={reportTypes}
              onReportTypeChange={setReportType}
              onGenerate={handleGenerateMasterReport}
              onClear={() => setSelectionModel([])}
            />
          )}
        </PrivateRoute>
      </Switch>

      {masterReportOpen && (
        <MasterReportDialog
          open={masterReportOpen}
          inspectionIds={selectionModel}
          reportType={reportType}
          onClose={handleMasterReportClose}
        />
      )}

      <ControlledSnackbar state={snackbarState} onClose={handleSnackbarClose} />

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Inspection
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this inspection? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="secondary" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}

InspectionList.propTypes = {};

export default InspectionList;
